import { useFilterStore } from "@/stores/filterStore"
import { useStore } from "@/stores/store";
import { ISearchResultItem } from "interfaces/search/ISearchResultItem";
import { computed } from "vue";
import { columnDefinitions } from "@/interfaces/search/columnDefinitions";
import { InputControls } from "@/interfaces/search/InputControls";
import { useSearch } from "./search-service";

export const useFilterApply = () => {
  const filterStore = useFilterStore();
  const generalStore = useStore();
 const { performSearch } = useSearch();

  const activeFilters = computed(() => filterStore.activeFilters);
  const activeSearchFilters = computed(() => filterStore.activeSearchFilters);

  const applyFilters = () => {
    generalStore.searchResult = generalStore.originalSearchResult;

    if (activeFilters.value.length === 0 && activeSearchFilters.value.length === 0) {
      return;
    }

    for (const filter of activeFilters.value) {
      const inputControl = getInputControl(filter.dataKey);
      switch (inputControl) {
        case InputControls.DROPDOWN: {
          if (Array.isArray(filter.dataKey)) {
            filterMultiColumn(filter.dataKey, filter.value);
          }
          else {
            filterColumn(filter.dataKey, filter.value);
          }
          break;
        }
        case InputControls.INTERVAL: {
          if (typeof filter.value === 'object' && (filter.value.min != null || filter.value.max != null)) {
            filterInterval(filter.dataKey, filter.value.min, filter.value.max);
          }
          break;
        }
        default:
          console.warn(`No filtering logic defined for input control: ${inputControl}`);
      }
    }

    for (const query of activeSearchFilters.value) {
      performSearch(query);
    }
  }

  function getInputControl(dataKey: string | string[]): InputControls {
    // We assume that if it's an array, both keys have the same input control. If not, you're using it wrong.
    const column = Array.isArray(dataKey) ? dataKey[0] : dataKey;
    return columnDefinitions[column]?.inputControl || InputControls.NONE;
  }

  const filterInterval = (dataKey: string, min: number | null, max: number | null) => {
    if (!generalStore.searchResult) {
      return;
    }

    const filteredResults = generalStore.searchResult?.results.filter((item: ISearchResultItem) => {
      const itemValue = item[dataKey];
      let isWithinRange = true;

      const numericItemValue = typeof itemValue === 'number' ? itemValue : Number(itemValue);

      if (min !== null && min !== undefined) {
        isWithinRange = isWithinRange && numericItemValue >= min;
      }

      if (max !== null && max !== undefined) {
        isWithinRange = isWithinRange && numericItemValue <= max;
      }

      return isWithinRange;
    });

    generalStore.searchResult = { ...generalStore.searchResult, results: filteredResults };
  };

  const filterColumn = (key: string, filterValues: string | string[]) => {
    if (!generalStore.searchResult) {
      return;
    }

    // Ensure filterValues is an array to simplify logic
    const filterValuesArray = Array.isArray(filterValues) ? filterValues : [filterValues];

    const filteredResults = generalStore.searchResult.results.filter((item: ISearchResultItem) => {
      const itemValue = item[key];
      // No value for this column? Exclude
      if (!itemValue) {
        return false;
      }

      // Convert item value to lowercase string
      const itemValueStr = itemValue.toString().toLowerCase();
      // Check if any filterValues match the itemValue
      return filterValuesArray.some(filterValue => itemValueStr.includes(filterValue.toLowerCase()));
    });

    generalStore.searchResult = { ...generalStore.searchResult, results: filteredResults };
    generalStore.currentPage = 0;
  };

  const filterMultiColumn = (keys: string[], filterValues: string|string[]) => {
    if (!generalStore.searchResult) {
      return;
    }

    const values = Array.isArray(filterValues) ? filterValues : [filterValues];
    const out = generalStore.searchResult.results.filter((item: ISearchResultItem) => {
      // keep item if *any* key matches *any* selected value
      return keys.some(key => {
      const v = (item as any)[key];
      if (typeof v === 'string') {
        return values.some(val => v.toLowerCase().includes(val.toLowerCase()));
      }
      if (Array.isArray(v)) {
        return v.some(s => values.some(val => s.toLowerCase().includes(val.toLowerCase())));
      }
      return false;
      });
    });

    generalStore.searchResult = { ...generalStore.searchResult, results: out };
    generalStore.currentPage = 0;
  }

  return {
    applyFilters,
    filterInterval,
    filterColumn,
  }
}